<script lang="ts" setup>import { computed as _computed } from 'vue';

import { Select, AppButton } from '@pohlcon/design-system';
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FIRE_PROTECTION_OPTIONS,
  HEIGHT_OPTIONS,
  ISOPRO,
  ISOPROHeight,
  ISOPROLengths,
  ISOPROLoadBearingCapacity,
  LENGTH_OPTIONS,
  // CONCRETE_COVERING_OPTIONS,
  LOAD_BEARING_CAPACITY_OPTIONS,
  SHEAR_LOAD_FORCE,
  TYPE_OPTIONS,
  VARIANT_OPTIONS,
} from '~/models';
import { FilterValues, useProductsStore } from '~/store';
import { filterISOPROProducts } from './models';
import { getElementThresholdHeight, getElementThresholdLBC } from './capacities';
import { ShortElementTypeDTOTypeEnum, SteelSMTypeDTOTypeEnum, SteelSQTypeDTOTypeEnum } from '~/api/webservice';
import { MessageSchema } from '~/locales';

const { t } = useI18n<{ message: MessageSchema; }>();
const store = useProductsStore();

const props = defineProps<{
  products: ISOPRO[];
}>();

const emits = defineEmits<{
  (e: 'emptyFilterList'): void;
}>();

const data = reactive({
  type: Object.entries(TYPE_OPTIONS)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })).sort((a, b) => a.value.localeCompare(b.value)),
  loadBearingCapacity: Object.entries(LOAD_BEARING_CAPACITY_OPTIONS)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })),
  height: Object.entries(HEIGHT_OPTIONS)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })),
  shearForce: Object.entries(SHEAR_LOAD_FORCE)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })),
  fireProtection: Object.entries(FIRE_PROTECTION_OPTIONS)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })),
  length: Object.entries(LENGTH_OPTIONS)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })),
  variant: Object.entries(VARIANT_OPTIONS)
    .map(([k, v]) => ({ text: t(v), value: k.toString() })),
  // concreteCovering will be ignored for now but may be relevant in future versions
});

const filteredProducts = _computed(() => {
  const { type, loadBearingCapacity, height, shearForce, fireProtection, length, variant } = filterValues.value;
  const filterProd = filterISOPROProducts(props.products, {
    type: type === '_none' ? null : type,
    loadBearingCapacity: loadBearingCapacity === '_none' ? null : (+loadBearingCapacity as ISOPROLoadBearingCapacity),
    height: height === '_none' ? null : (+height as ISOPROHeight),
    shearForce: shearForce === '_none' ? null : shearForce,
    fireProtection: fireProtection === '_none' ? null : (fireProtection === 'true'),
    length: length === "_none" ? null : (+length as ISOPROLengths),
    variant: variant === "_none" ? null : variant
  });
  if (!filterProd) emits('emptyFilterList');
  return filterProd;
});

const filterValues = _computed({
  get () {
    return store.filterValues;
  },
  set (value: FilterValues) {
    store.filterValues = value;
  }
});

const filteredType = _computed(() => {
  switch (filterValues.value['type']) {
    case 'D':
    case 'M':
      return 'bendingMoment';
    case 'SM':
    case 'SQ':
      return 'steelElement';
    case 'Q':
    case 'QQ':
    case 'QZ':
      return 'shearForce';
    case 'QS':
    case 'QQS':
    case 'QQSZ':
    case 'QSZ':
      return 'shortElement';
    default:
      return '_none';
  }
});
// QS, QSZ, QQS, QQSZ
// const shortElementSelected = $computed(() =>
//   filterValues["type"] !== "_none"
//     ? ["QS", "QSZ", "QQS", "QQSZ"].includes(filterValues["type"])
//     : false
// );
// // Q, QQ, QZ
// const shearForceElementSelected = $computed(() =>
//   filterValues['type'] !== '_none'
//     ? ["Q", "QQ", "QZ"].includes(filterValues['type'])
//     : false
// );

// // SM, SQ
// const steelElementSelected = $computed(() => filterValues['type'] !== '_none'
//   ? ['SM', 'SQ'].includes(filterValues['type'])
//   : false
// );

// M, SM
const heightOffsetElementTypeSelected = _computed(() => ['M', 'SM'].includes(filterValues.value.type));

const variantSelected = _computed(() => filterValues.value['variant'] !== '_none');

const allowedLoadBearingValues = _computed(() => {
  const DKeys = ['20', '30', '50', '70', '90', '100'];
  const steelSpecificKeys = ['8', '12', '14'];
  const SMKeys = ['14', '20'];
  const SQKeys = ['8', '10', '12'];
  const generalLoadBearingValues = Object.keys(LOAD_BEARING_CAPACITY_OPTIONS)
    .filter((k) => !steelSpecificKeys.includes(k))
    .map((k) => k.toString());

  switch (filterValues.value.type) {
    case 'D':
      return DKeys;
    case 'SM':
      return SMKeys;
    case 'SQ':
      return SQKeys;
    default:
      return generalLoadBearingValues;
  }
});

function getOptions (name: string, filterOption: { text: string, value: string, isLocked?: boolean; }[]) {
  let options = filterOption;
  switch (name) {
    case 'loadBearingCapacity':
      // 120 is the max. LBC, therefore we start with all lbcs available
      let thresholdLoadBearingCapacity: ISOPROLoadBearingCapacity | null = 120;
      let adaptedLoadBearingValues = allowedLoadBearingValues.value;
      if (filteredType.value === 'shortElement') {
        thresholdLoadBearingCapacity = getElementThresholdLBC(filterValues.value, "shortElement");
      } else if (filteredType.value === 'shearForce') {
        thresholdLoadBearingCapacity = getElementThresholdLBC(filterValues.value, 'shearForceElement');
      } else if (variantSelected.value) {
        thresholdLoadBearingCapacity = getElementThresholdLBC(filterValues.value, 'variantElement');
      }

      // console.log('thresholdLoadBearingCapacity', thresholdLoadBearingCapacity);
      options.forEach((option) => {
        if (!!thresholdLoadBearingCapacity && +option.value > thresholdLoadBearingCapacity)
          adaptedLoadBearingValues = adaptedLoadBearingValues
            .filter((allowedValue, i) => allowedValue !== option.value);
        const isAllowedLoadBearingValue = adaptedLoadBearingValues.includes(option.value) || option.value === '_none';
        option.isLocked = !isAllowedLoadBearingValue;
      });

      if (!allowedLoadBearingValues.value.includes(filterValues.value['loadBearingCapacity']))
        filterValues.value['loadBearingCapacity'] = '_none';
      break;

    case 'shearForce':
      const shearForceDKeys = ['Q6', 'Q8', 'Q10'];
      const generalShearForceValues = Object.keys(SHEAR_LOAD_FORCE)
        .filter((k) => k !== 'QQ')
        .map((k) => k.toString());
      const generalSteelShearForceValues = ['Q8', 'Q10', 'Q12', 'QQ'];
      let allowedShearForceValues = generalShearForceValues;
      if (filterValues.value.type === 'D') {
        allowedShearForceValues = +filterValues.value['height'] === 160
          ? (+filterValues.value['loadBearingCapacity'] === 100
            ? ['Q6']
            : ['Q6', 'Q8'])
          : shearForceDKeys;
        if (+filterValues.value['height'] === 170 && +filterValues.value['loadBearingCapacity'] === 100)
          allowedShearForceValues = ['Q6', 'Q8'];
      } else if (filterValues.value.type === 'M' && filterValues.value.variant !== '_none') {
        allowedShearForceValues = ['Q4', 'Q6'];
      } else if (filterValues.value.type === 'SM') {
        allowedShearForceValues = generalSteelShearForceValues;
      };

      options.forEach((option) => {
        const isAllowedShearForceValue = allowedShearForceValues.includes(option.value) || option.value === '_none';
        option.isLocked = !isAllowedShearForceValue;
      });
      if (!allowedShearForceValues.includes(filterValues.value['shearForce'])) filterValues.value['shearForce'] = '_none';
      break;

    case 'height':
      if (filteredType.value === 'steelElement') {
        const steelThresholdHeight = 180;
        options.forEach((option) => {
          if (+option.value < steelThresholdHeight) option.isLocked = true;
        });
      } else if (filterValues.value['loadBearingCapacity'] !== '_none') {
        let thresholdHeight: ISOPROHeight | null = null;
        if (filteredType.value === 'shortElement') {
          thresholdHeight = getElementThresholdHeight(filterValues.value, 'shortElement');
        } else if (filteredType.value === 'shearForce') {
          thresholdHeight = getElementThresholdHeight(filterValues.value, 'shearForceElement');
        } else if (variantSelected.value) {
          thresholdHeight = getElementThresholdHeight(filterValues.value, 'variantElement');
        }

        options.forEach((option) => {
          if (!!thresholdHeight && +option.value < thresholdHeight) {
            option.isLocked = true;
          } else {
            option.isLocked = false;
          }
          if (thresholdHeight && +filterValues.value['height'] < thresholdHeight)
            filterValues.value['height'] = '_none';
        });
      } else {
        options.forEach((option) => option.isLocked = false);
      }
      if (filterValues.value['type'] === 'D') {
        let blockedHeights: number[] = [];
        if (filterValues.value['shearForce'] === 'Q10') {
          blockedHeights.push(160);
          if (+filterValues.value['loadBearingCapacity'] === 100) {
            blockedHeights.push(170);
          }
        } else if (filterValues.value['shearForce'] === 'Q8'
          && +filterValues.value['loadBearingCapacity'] === 100) blockedHeights.push(160);

        options.forEach((option) => {
          if (blockedHeights.includes(+option.value)) option.isLocked = true;
          else option.isLocked = false;
        });
      }
      break;
    case 'type':
      if (filterValues.value['length'] === '1000') {
        options.forEach((option) => {
          if (Object.values(ShortElementTypeDTOTypeEnum)
            .includes(option.value as ShortElementTypeDTOTypeEnum)
            || Object.values({
              ...SteelSMTypeDTOTypeEnum,
              ...SteelSQTypeDTOTypeEnum
            }).includes(option.value as SteelSMTypeDTOTypeEnum || option.value as SteelSQTypeDTOTypeEnum)) {
            option.isLocked = true;
          } else {
            option.isLocked = false;
          }
        });
      } else if (filterValues.value['length'] === '220') {
        options.forEach((option) => {
          if (Object.values({
            ...SteelSMTypeDTOTypeEnum,
            ...SteelSQTypeDTOTypeEnum
          }).includes(option.value as SteelSMTypeDTOTypeEnum || option.value as SteelSQTypeDTOTypeEnum) || option.value === '_none') {
            option.isLocked = false;
          } else {
            option.isLocked = true;
          }
        });
        // if length is not 1000mm OR 220mm (steelTypes) then it has to be a shortElement and therefore all non short elements get locked
      } else if (filterValues.value['length'] !== '_none') {
        options.forEach((option) => {
          if (![...Object.values(ShortElementTypeDTOTypeEnum), '_none']
            .includes(option.value as ShortElementTypeDTOTypeEnum)) {
            option.isLocked = true;
          } else {
            option.isLocked = false;
          }
        });
      } else {
        options.forEach((option) => option.isLocked = false);
      }
      break;
    case 'length':
      if (filteredType.value === 'shortElement') {
        options.forEach((option) => {
          if (['1000', '220'].includes(option.value)) option.isLocked = true;
          else option.isLocked = false;
        });
      } else if (filteredType.value === 'steelElement') {
        options.forEach((option) => {
          if (option.value === '220') option.isLocked = false;
          else option.isLocked = true;
        });
      } else {
        options.forEach((option) => {
          if (['1000', '_none'].includes(option.value)) option.isLocked = false;
          else option.isLocked = true;
        });
      }
    case 'variant':
      if (filteredType.value === 'steelElement') {
        options.forEach((option) => {
          if (option.value === 'HV') option.isLocked = true;
        });
      } else {
        options.forEach((option) => {
          if (option.value === 'HV') option.isLocked = false;
        });
      }
      break;
  };
  return options;
}

function checkDisabled (name: string): boolean {
  let check = false;
  switch (name) {
    case 'shearForce':
      // reset shearForceLoadBearingCapacity selection when shearForce element is selected
      // shearForce elements are also handled by the loadBearingCapacity dropdown
      check = ['Q', 'QQ', 'QZ', 'QS', 'QQS', 'QQSZ', 'QSZ', 'SQ'].includes(filterValues.value.type);
      if (check) filterValues.value['shearForce'] = '_none';
      return check;
    case 'variant':
      // reset variant value for non variant types selected
      check = ![
        'M',
        'SM',
        '_none'
      ].includes(filterValues.value.type);
      if (check) filterValues.value.variant = '_none';
      return check;
    case 'fireProtection':
      check = filteredType.value === 'steelElement';
      // deactivate fireProtection selection and set to false for steel elements
      if (filteredType.value === 'steelElement') filterValues.value['fireProtection'] = '_none';
      return check;
  }
  return check;
}

watch(() => filterValues.value.type, () => {
  if (!heightOffsetElementTypeSelected.value) filterValues.value.variant = '_none';
  if (filteredType.value === 'steelElement') {
    if (filterValues.value.variant === 'HV') filterValues.value.variant = '_none';
    if (['160', '170'].includes(filterValues.value.height)) filterValues.value['height'] = '_none';
    if (filterValues.value['type'] === 'SQ') filterValues.value['shearForce'] = '_none';
  }
});

</script>

<template>
  <header class="choices-wrapper wrapper">
    <Select
      v-for="(filterOption, name) in data"
      v-model="filterValues[name]"
      :key="name"
      :options="getOptions(name, filterOption)"
      :label="t(`filter.title.${name}`)"
      :disabled="checkDisabled(name)"
      :groupDisabledOptions="true"
      :sortOptions="{ order: 'asc', target: 'value', type: 'numerical', ignoreCase: true }"
      :cypress-id="`product-filter-${name}`"
    />
    <AppButton
      :icon="'close'"
      @click="store.resetFilterValues()"
      :text="t('actions.reset')"
    />
  </header>
  <slot :filteredProducts="filteredProducts" />
</template>


<style lang="scss">
@use '../../../variables' as *;

.choices-wrapper {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: end;
  gap: var(--gutter-m);
  line-height: 1.6;
  margin-bottom: 20px;
}

@media screen and (max-width: $breakpoint-nav) {
  .choices-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
